import { type ComponentType, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import type { ComposeEmailLayerProps } from '../features/email/compose/ComposeEmailLayer';
import {
    type ComposeEmailComposition,
    ComposeEmailContext,
    type ComposeEmailRequest,
    type OpenComposeEmail,
} from '../lib/email/composeEmail';
import { Toast } from '../ui';
import { composeEmailComposition } from './composeEmailComposition';
import { focusPageHeading } from './PageHeadingFocus';

/**
 * Supplies the compose opener (`useComposeEmail()`, `src/lib/email/composeEmail.ts`) to the whole
 * authenticated app (F20-c FR-O1; WI-1.2 D-10–D-12). Mounted once, in `AppLayout`, as its outermost
 * element — outside the column that turns `inert` while the tablet drawer is open, so the dialog is
 * never inside an inert subtree.
 *
 * - **The layer loads on the first open, with `import()` — not `React.lazy`**
 *   (F20-c WI-1.2 plan D-11). The loaded module is kept in state, so a load that failed (offline) is
 *   tried again on the next open, where a rejected `lazy()` would stay rejected; a failure shows a §5
 *   `Toast` and calls `onClosed`. While the chunk loads nothing renders, so focus stays on the button
 *   that opened it.
 * - **One dialog at a time.** An open while one is open (or loading) is ignored. An open while one is
 *   closing is kept and runs once it has finished: the closed dialog is hidden and focus is back on its
 *   opener before its native `close` event reports the end, so a keyboard user can press *Compose*
 *   again inside that gap, and on a busy machine the key can be handled first (BUG-134).
 * - **Focus returns to the opener** (F20-c WI-1.2 plan D-12). The native dialog returns it in a
 *   browser; when the layer reports the dialog finished and focus is on `<body>` or on a removed
 *   element, this focuses the recorded opener — the jsdom case, and an opener that re-rendered. When
 *   the opener has gone too (a reply whose source answered `404` turns the message view to not-found,
 *   removing *Reply*), focus goes to the page's heading in `<main>` (`focusPageHeading`).
 */

/** The layer's chunk, as `import()` resolves it — exported for its test (the `accountPanels.ts` pattern). */
export const loadComposeEmailLayer = () => import('../features/email/compose/ComposeEmailLayer');

type LayerModule = { readonly default: ComponentType<ComposeEmailLayerProps> };

export const LOAD_FAILED_MESSAGE =
    'The email window couldn’t be opened. Check your connection and try again.';

export interface ComposeEmailProviderProps {
    readonly children: ReactNode;
    /** The layer loader; tests pass one that fails. */
    readonly load?: () => Promise<LayerModule>;
    /** What fills the dialog's `aiDraft` place; the app's composition unless a test passes one. */
    readonly composition?: ComposeEmailComposition;
}

export function ComposeEmailProvider({
    children,
    load = loadComposeEmailLayer,
    composition = composeEmailComposition,
}: ComposeEmailProviderProps) {
    const [Layer, setLayer] = useState<ComponentType<ComposeEmailLayerProps> | null>(null);
    const [current, setCurrent] = useState<{
        readonly id: number;
        readonly request: ComposeEmailRequest;
    } | null>(null);
    const [loadFailed, setLoadFailed] = useState(false);
    const busy = useRef(false);
    const closing = useRef(false);
    const pending = useRef<{ readonly request: ComposeEmailRequest; readonly opener: Element | null } | null>(
        null,
    );
    const nextId = useRef(0);
    const opener = useRef<Element | null>(null);
    const returnFocus = useRef(false);
    const layerRef = useRef<ComponentType<ComposeEmailLayerProps> | null>(null);
    const loadRef = useRef(load);
    useEffect(() => {
        loadRef.current = load;
    }, [load]);

    const start = useCallback((request: ComposeEmailRequest, openedFrom: Element | null) => {
        busy.current = true;
        opener.current = openedFrom;
        nextId.current += 1;
        const id = nextId.current;
        const show = () => setCurrent({ id, request });
        if (layerRef.current !== null) {
            show();
            return;
        }
        loadRef.current().then(
            (module) => {
                layerRef.current = module.default;
                setLayer(() => module.default);
                show();
            },
            () => {
                busy.current = false;
                setLoadFailed(true);
                request.onClosed?.();
            },
        );
    }, []);

    const open = useCallback<OpenComposeEmail>(
        (request) => {
            if (busy.current) {
                if (closing.current) {
                    pending.current = { request, opener: document.activeElement };
                }
                return;
            }
            start(request, document.activeElement);
        },
        [start],
    );

    const onClosing = useCallback(() => {
        closing.current = true;
    }, []);

    const finished = useCallback(() => {
        busy.current = false;
        closing.current = false;
        returnFocus.current = true;
        setCurrent(null);
        const next = pending.current;
        pending.current = null;
        if (next !== null) {
            start(next.request, next.opener);
        }
    }, [start]);

    // Once the finished dialog has gone, put focus back on the opener if nothing holds it, or on the
    // page heading when the opener has left the page.
    useEffect(() => {
        if (current !== null || !returnFocus.current) {
            return;
        }
        returnFocus.current = false;
        const active = document.activeElement;
        const lost = active === null || active === document.body || !active.isConnected;
        const target = opener.current;
        if (!lost) {
            return;
        }
        // A click in Safari focuses nothing, so the recorded opener can be `<body>`: that is no opener.
        if (target instanceof HTMLElement && target !== document.body && target.isConnected) {
            target.focus();
            return;
        }
        const main = document.querySelector('main');
        if (main !== null) {
            focusPageHeading(main);
        }
    }, [current]);

    return (
        <ComposeEmailContext.Provider value={open}>
            {children}
            {Layer === null ? null : (
                <Layer
                    composition={composition}
                    onClosing={onClosing}
                    onFinished={finished}
                    request={current?.request ?? null}
                    requestId={current?.id ?? 0}
                />
            )}
            <Toast
                data-testid="email-compose-load-failed-toast"
                message={LOAD_FAILED_MESSAGE}
                onDismiss={() => setLoadFailed(false)}
                open={loadFailed}
            />
        </ComposeEmailContext.Provider>
    );
}
